import React, { useState, useEffect, useRef } from "react";
import BackButtonComponent from "../../backButton";
import EditIcon from "../../../assets/images/custom_documentation/file_name_edit.svg";
import ClearIcon from "../../../assets/images/custom_documentation/delete.svg";
import CreateTaskIcon from "../../../assets/images/custom_documentation/create_task.svg";
import SaveIcon from "../../../assets/images/custom_documentation/save.svg";
import PreviewIcon from "../../../assets/images/custom_documentation/Preview.svg";
import RenameTaskModal from "../Modals/renameTaskModal";
import ClearTaskModal from "../Modals/clearTaskModal";
import CreateTaskModal from "../Modals/createTaskModal";
// import SubmitModal from "./submitModal";
import LeaveConfirmationModal from "../Modals/leaveConfirmationModal";
import PreviewModal from "../Modals/PreviewModal";
import { toast } from "react-toastify";
import { putData, postData } from "../../../api";
import "./style.scss";
import CancelIcon from "../../../assets/images/custom_documentation/Cancel.svg";
import { useNavigate } from "react-router-dom";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { checkMarkBase64 } from "../../CustomPdf/Inputs/checkMarkBase64";

const Header = ({
  onClearFields,
  hasFields = false,
  documentData,
  documentName,
  setDocumentName,
  placedFields,
  canvasSettings,
  modalSettingsObj,
  hasUnsavedChanges,
  onSaveSuccess,
  onPreviewClose,
  viewMode = false,
  taskViewMode = false,
  onPreview,
  onTaskSubmit,
  forceRedirectToClientsDocuments = false,
}) => {
  const navigate = useNavigate();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showConfirmClearModal, setShowConfirmClearModal] = useState(false);
  //const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showLeaveConfirmationModal, setShowLeaveConfirmationModal] =
    useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isHeaderTouched, setIsHeaderTouched] = useState(false);
  const headerRef = useRef(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewFields, setPreviewFields] = useState([]);

  const handleDownloadPdf = async () => {
    try {
      if (!documentData?.design_url) {
        toast.error("No document available for download", {
          theme: "colored",
          autoClose: 3000,
        });
        return;
      }

      // For taskViewMode, generate PDF with field values
      if (taskViewMode && placedFields && placedFields.length > 0) {
        await generatePdfWithFields();
      } else {
        // For view mode, download original PDF
        const response = await fetch(documentData.design_url);
        const pdfBlob = await response.blob();

        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(pdfBlob);
        downloadLink.download = `${documentName || "document"}.pdf`;
        downloadLink.click();
        URL.revokeObjectURL(downloadLink.href);
      }
    } catch (error) {
      toast.error("Failed to download PDF", {
        theme: "colored",
        autoClose: 3000,
      });
    }
  };

  const generatePdfWithFields = async () => {
    try {
      if (!documentData?.design_url) {
        toast.error("No PDF file available", { theme: "colored", autoClose: 3000 });
        return;
      }
      
      const response = await fetch(documentData.design_url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }
      
      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      // Group fields by page number
      const fieldsByPage = {};
      placedFields.forEach((field) => {
        const pageNum = field.pageNumber?.toString() || "1";
        if (!fieldsByPage[pageNum]) {
          fieldsByPage[pageNum] = [];
        }
        fieldsByPage[pageNum].push(field);
      });
      
      for (const pageKey in fieldsByPage) {
        const pageFields = fieldsByPage[pageKey];
        const pageIndex = parseInt(pageKey) - 1;
        
        // Skip if page doesn't exist in the PDF
        if (pageIndex < 0 || pageIndex >= pdfDoc.getPageCount()) {
          console.warn(`Skipping page ${pageKey} - PDF only has ${pdfDoc.getPageCount()} pages`);
          continue;
        }
        
        const page = pdfDoc.getPage(pageIndex);
        
        for (const field of pageFields) {
          const type = field.type || field.inputType || "text";
          const top = field.top || field.y || 0;
          const left = field.left || field.x || 0;
          const width = field.width || 100;
          const height = field.height || 30;
          const value = field.value || "";
          const fontSize = parseFloat(field.font_size || field.fontSize) || 12;
          const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
          const zIndex = 99;

          let positionX, positionY;
          
          // Map field types to PDF types
          const fieldTypeMapping = {
            "Text input": "text",
            "Text mulit line input": "textarea",
            "Date": "date",
            "Dropdown": "select",
            "Checkbox": "checkbox",
            "Radio": "radio",
            "Initials": "initials",
            "Signature": "signature",
          };
          
          const pdfFieldType = fieldTypeMapping[type] || type.toLowerCase();
          
          // Use exact UI coordinates with proper PDF coordinate system transformation
          // PDF uses bottom-left origin, UI uses top-left origin
          if (pdfFieldType === "checkbox" || pdfFieldType === "radio") {
            // Radio/Checkbox are center-aligned in UI using flexbox
            // Position the element at the center of the container
            const elementSize = (pdfFieldType === "checkbox") ? 10 : 7; // Current sizes in points
            positionX = left + (width / 2) - (elementSize / 2); // Center horizontally
            positionY = page.getHeight() - top - (height / 2) - (elementSize / 2); // Center vertically
            
            // Move radio fields up by 5px (3.75 points) only
            if (pdfFieldType === "radio") {
              positionY += 3.75;
              positionX += 7.5; // Move radio fields 10px to the right
            }
          } else if (pdfFieldType === "textarea") {
            // Textarea should start from top of container, not bottom
            positionX = left + 3.75; // 5px padding converted to points (5 * 0.75)
            positionY = Math.max(0, page.getHeight() - top); // Start from top, prevent negative values
          } else {
            // For all other fields, use exact UI coordinates with 5px padding
            positionX = left + 3.75; // 5px padding converted to points (5 * 0.75)
            positionY = page.getHeight() - top - height;
          }
          
          if (pdfFieldType === "text") {
            page.drawText(String(value || ""), {
              x: positionX,
              y: positionY + fontSize, // Adjust for font baseline
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (pdfFieldType === "signature") {
            if (value && value !== "[Signature]") {
              let signatureDataUrl = null;
              
              // Handle case where value is a JSON string (from eSignature modal)
              if (typeof value === 'string') {
                try {
                  // Try to parse as JSON first
                  const parsed = JSON.parse(value);
                  if (parsed.signature && parsed.signature.startsWith('data:image/')) {
                    signatureDataUrl = parsed.signature;
                  }
                } catch (e) {
                  // If parsing fails, check if it's a direct data URL
                  if (value.startsWith('data:image/')) {
                    signatureDataUrl = value;
                  }
                }
              }
              
              // Display signature if we have valid data
              if (signatureDataUrl) {
                try {
                  const pdfImage = await pdfDoc.embedPng(signatureDataUrl);
                  page.drawImage(pdfImage, {
                    x: positionX,
                    y: positionY,
                    width: width || 40,
                    height: height || 40,
                    zIndex: zIndex,
                  });
                } catch (sigError) {
                  // Continue without signature if it fails
                  console.warn('Failed to embed signature image:', sigError);
                }
              }
            }
          } else if (pdfFieldType === "initials") {
            page.drawText(String(value || "").toUpperCase(), {
              x: positionX,
              y: positionY + fontSize, // Adjust for font baseline
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (pdfFieldType === "checkbox") {
            const checkboxSize = 10;
            page.drawRectangle({
              x: positionX,
              y: positionY,
              width: checkboxSize,
              height: checkboxSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            if (value) {
              try {
                const pdfImage = await pdfDoc.embedPng(checkMarkBase64);
                page.drawImage(pdfImage, {
                  x: positionX,
                  y: positionY,
                  width: checkboxSize,
                  height: checkboxSize,
                  zIndex: zIndex,
                });
              } catch (checkError) {
                // Continue without checkmark if it fails
              }
            }
          } else if (pdfFieldType === "textarea") {
            const textValue = value || "";
            
            // Use a very conservative approach - calculate max characters per line
            const avgCharWidth = fontSize * 0.55; // More conservative character width
            const maxCharsPerLine = Math.floor((width - 20) / avgCharWidth); // Extra padding
            
            // Function to wrap text based on character count, breaking regardless of spaces
            const wrapText = (text) => {
              const lines = [];
              let currentLine = '';
              
              for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const testLine = currentLine + char;
                
                // Use character count as primary constraint
                if (testLine.length > maxCharsPerLine && currentLine) {
                  lines.push(currentLine);
                  currentLine = char;
                } else {
                  currentLine = testLine;
                }
              }
              
              if (currentLine) {
                lines.push(currentLine);
              }
              
              return lines;
            };
            
            // Handle manual line breaks and word wrapping
            const lines = textValue.split('\n');
            const allLines = [];
            
            lines.forEach(line => {
              if (line.trim()) {
                const wrappedLines = wrapText(line);
                allLines.push(...wrappedLines);
              }
            });

            allLines.forEach((line, index) => {
              page.drawText(line, {
                x: positionX,
                y: positionY - fontSize - index * fontSize, // Position lines downward from top
                size: fontSize,
                font,
                zIndex: zIndex,
              });
            });
          } else if (pdfFieldType === "select") {
            const selectValue = Array.isArray(value)
              ? value.join(", ")
              : value || "";
            page.drawText(String(selectValue), {
              x: positionX,
              y: positionY + fontSize, // Adjust for font baseline
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (pdfFieldType === "radio") {
            const radioSize = 7;
            page.drawEllipse({
              x: positionX,
              y: positionY,
              xScale: radioSize,
              yScale: radioSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            // Radio group logic: Only check this specific radio field if it's the selected one
            // Radio fields should be mutually exclusive - only one can be true
            const isThisFieldSelected = value === true && value !== null;
            
            if (isThisFieldSelected) {
              page.drawEllipse({
                x: positionX,
                y: positionY,
                xScale: radioSize - 2,
                yScale: radioSize - 2,
                color: rgb(0, 0, 0),
                zIndex: zIndex,
              });
            }
          } else if (pdfFieldType === "date") {
            // Format date to MM-DD-YYYY
            let formattedDate = "";
            if (value) {
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const year = date.getFullYear();
                  formattedDate = `${month}-${day}-${year}`;
                } else {
                  formattedDate = String(value);
                }
              } catch (error) {
                formattedDate = String(value);
              }
            }
            page.drawText(formattedDate, {
              x: positionX,
              y: positionY + fontSize, // Adjust for font baseline
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          }
        }
      }
      
      const modifiedPdfBytes = await pdfDoc.save();
      const modifiedPdfBlob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(modifiedPdfBlob);
      downloadLink.download = `${documentName || "document"}_filled.pdf`;

      downloadLink.click();
    } catch (error) {
      toast.error("Failed to generate PDF preview: " + error.message, {
        theme: "colored",
        autoClose: 3000,
      });
    }
  };
  const handleDocumentNameUpdate = async (newName) => {
    try {
      const payload = {
        name: newName,
        client_document_id: documentData?.id,
      };

      const response = await putData(
        "custom-client-document-update-name",
        {},
        payload,
      );

      if (response.status === true) {
        setDocumentName(response.data?.name);
        toast.success(response.message, {
          theme: "colored",
          autoClose: 3000,
        });

        // Call onSaveSuccess to update original state after name change
        // Use setTimeout to ensure state is updated before checking
        setTimeout(() => {
          if (onSaveSuccess) {
            onSaveSuccess();
          }
        }, 0);
      } else {
        toast.error(response.message || "Failed to update document name", {
          theme: "colored",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Error updating document name", {
        theme: "colored",
        autoClose: 3000,
      });
    }
  };

  const handleClear = () => {
    setShowConfirmClearModal(true);
  };
  const handleConfirmClear = () => {
    setShowConfirmClearModal(false);
    if (onClearFields) {
      onClearFields();
    }
  };
  const handleSubmit = async () => {
    try {
      // Prepare the payload according to the API specification
      const settings = {};

      // Group fields by page number
      placedFields.forEach((field) => {
        const pageNum = field.pageNumber?.toString() || "1";
        if (!settings[pageNum]) {
          settings[pageNum] = {};
        }

        // Map field types to API types
        const typeMapping = {
          "Text input": "text",
          "Text mulit line input": "textarea",
          Date: "date",
          Dropdown: "select",
          Checkbox: "checkbox",
          Radio: "radio",
          Initials: "initials",
          Signature: "signature",
        };

        const apiType = typeMapping[field.type] || field.inputType || "text";
        const fieldId = settings[pageNum]
          ? Object.keys(settings[pageNum]).length + 1
          : 1;

        const fieldData = {
          type: apiType,
          scale: field.scale || 1,
          top: Math.round(field.y || 0),
          left: Math.round(field.x || 0),
          value: field.value || "",
          place_holder: apiType === "select" 
            ? "Select an option"
            : (field.value && typeof field.value === "string" && field.value.trim()
              ? field.value
              : field.placeholder || ""),
          width: field.width || 100,
          height: field.height || 30,
          font_size: field.fontSize,
          owner: "admin",
          needed: field.needed || false,
          font_style: field.fontStyle || field.font_style,
          character_limit: field.characterLimit || 0,
        };

        settings[pageNum][fieldId] = fieldData;

        // Add field-specific properties
        if (apiType === "select" && field.options) {
          settings[pageNum][fieldId].options = field.options;
          settings[pageNum][fieldId].multiple_options =
            field.multiple_options || false;
        }
        if (apiType === "radio" && field.radio_group) {
          settings[pageNum][fieldId].radio_group = field.radio_group;
        }
      });

      const payload = {
        document_id: documentData?.id,
        document_type: "client_document",
        settings: settings,
        canvas_settings: {
          scale: canvasSettings?.scale || 1,
        },
      };

      const response = await postData(
        "custom-client-document-save",
        {},
        payload,
      );

      if (response.status === true) {
        // Show success toast
        toast.success(response.message, {
          theme: "colored",
          autoClose: 3000,
        });

        // Call onSaveSuccess to update original state
        // Use setTimeout to ensure state is updated before checking
        setTimeout(() => {
          if (onSaveSuccess) {
            onSaveSuccess();
          }
        }, 0);

        // Move values to placeholders for text and textarea fields for preview
        const updatedFieldsForPreview = placedFields.map((field) => {
          if (
            (field.type === "text" ||
              field.type === "Text" ||
              field.type === "Text input" ||
              field.type === "textarea" ||
              field.type === "Text mulit line input") &&
            field.value &&
            typeof field.value === "string" &&
            field.value.trim()
          ) {
            return {
              ...field,
              placeholder: field.value, // Move value to placeholder
              value: "", // Clear the value
            };
          }
          return field;
        });

        // Set preview fields state
        setPreviewFields(updatedFieldsForPreview);

        // Set preview data and show modal
        setPreviewData(response.data);
        setShowPreviewModal(true);
      } else {
        toast.error(response.message || "Failed to save document", {
          theme: "colored",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Error saving document", {
        theme: "colored",
        autoClose: 3000,
      });
    }
  };
  // const handleSave = () => {
  //   setShowSubmitModal(false);
  // }

  const handleCreateTask = () => {
    setShowCreateTaskModal(true);
  };

  // const handleSaveTask = (taskData) => {
  //   console.log("Task created:", taskData);
  //   // Here you can add logic to save the task
  //   toast.success("Task created successfully", {
  //     theme: "colored",
  //     autoClose: 3000,
  //   });
  // };

  const handleBackClick = () => {
    if (viewMode) {
      checkPreviousPageAndRedirect();
      return;
    }

    if (hasUnsavedChanges && hasUnsavedChanges()) {
      setShowLeaveConfirmationModal(true);
    } else {
      checkPreviousPageAndRedirect();
    }
  };

  const checkPreviousPageAndRedirect = () => {
    const cakePhpBaseUrl = process.env.REACT_APP_CAKE_PHP_BASE_URL || '';
    const redirectRoute = 'clients/documents';
    
    // If force redirect is enabled, redirect directly to clients/documents
    if (forceRedirectToClientsDocuments) {
      window.location.href = cakePhpBaseUrl + redirectRoute;
      return;
    }
    
    // Default behavior: go back in history
    window.history.back();
  };

  const handleLeaveWithoutSaving = () => {
    setShowLeaveConfirmationModal(false);
    checkPreviousPageAndRedirect();
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    if (placedFields.length > 0 && onPreviewClose) {
      const updatedFields = placedFields.map((field) => {
        if (
          (field.type === "text" ||
            field.type === "Text" ||
            field.type === "Text input" ||
            field.type === "textarea" ||
            field.type === "Text mulit line input") &&
          field.value &&
          typeof field.value === "string" &&
          field.value.trim()
        ) {
          return {
            ...field,
            placeholder: field.value,
            value: "",
          };
        }
        return field;
      });
      onPreviewClose(updatedFields);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerRect = headerRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // Check if scrollTop touches the header (header is at the top of viewport)
        if (headerRect.top <= 0 && scrollTop > 0) {
          setIsHeaderTouched(true);
        } else {
          setIsHeaderTouched(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <div
        className={`custom-document-header ${isHeaderTouched ? "header-sticky" : ""}`}
        ref={headerRef}
      >
        <div className="left-column">
          <BackButtonComponent hasHistoryback onClick={handleBackClick} />
          <div className="file-name-edit-flex-wrapper">
            <p className="file-name-edit-text">{documentName}</p>
            {!viewMode && (
              <img
                src={EditIcon}
                alt="edit-icon"
                onClick={() => setShowRenameModal(true)}
                style={{ cursor: "pointer" }}
              />
            )}
          </div>
        </div>
        <div className="right-column">
          {taskViewMode ? (
            <>
              <div className="clear-button-wrapper">
                <button
                  className="clear-button"
                  onClick={() => navigate(-1)}
                >
                  <img src={CancelIcon} alt="cancel-icon" />
                  <p className="button-text">CANCEL</p>
                </button>
              </div>
              <div className="create-task-button-wrapper">
                <button className="create-task-button" onClick={handleDownloadPdf}>
                  <img src={PreviewIcon} alt="preview-icon" />
                  <p className="button-text">PREVIEW</p>
                </button>
              </div>
              <div className="save-submit-button-wrapper">
                <button className="save-button" onClick={onTaskSubmit}>
                  <img src={SaveIcon} alt="save-icon" />
                  <p className="button-text">SUBMIT</p>
                </button>
              </div>
            </>
          ) : viewMode ? (
            <>
              <div className="preview-button-wrapper">
                <button className="preview-button" onClick={handleDownloadPdf}>
                  <img src={PreviewIcon} alt="preview-icon" />
                  <p className="button-text">PREVIEW</p>
                </button>
              </div>
              <div className="create-task-button-wrapper">
                <button
                  className="create-task-button"
                  onClick={handleCreateTask}
                >
                  <img src={CreateTaskIcon} alt="create-task-icon" />
                  <p className="button-text">CREATE TASK</p>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="clear-button-wrapper">
                <button
                  className="clear-button"
                  onClick={handleClear}
                  disabled={!hasFields}
                >
                  <img src={ClearIcon} alt="clear-icon" />
                  <p className="button-text">CLEAR</p>
                </button>
              </div>
              <div className="create-task-button-wrapper">
                <button
                  className="create-task-button"
                  onClick={handleCreateTask}
                >
                  <img src={CreateTaskIcon} alt="create-task-icon" />
                  <p className="button-text">CREATE TASK</p>
                </button>
              </div>
              <div className="save-submit-button-wrapper">
                <button className="save-button" onClick={handleSubmit}>
                  <img src={SaveIcon} alt="save-icon" />
                  <p className="button-text">SAVE & PREVIEW</p>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <RenameTaskModal
        show={showRenameModal}
        onHide={() => setShowRenameModal(false)}
        documentName={documentName}
        onUpdate={handleDocumentNameUpdate}
        rename={true}
      />
      <ClearTaskModal
        show={showConfirmClearModal}
        onHide={() => setShowConfirmClearModal(false)}
        onClear={handleConfirmClear}
      />
      {/* <SubmitModal
        show={showSubmitModal}
        onHide={() => setShowSubmitModal(false)}
        onSave={handleSave}
      /> */}
      <LeaveConfirmationModal
        show={showLeaveConfirmationModal}
        onHide={() => setShowLeaveConfirmationModal(false)}
        onLeave={handleLeaveWithoutSaving}
      />
      <CreateTaskModal
        show={showCreateTaskModal}
        onHide={() => setShowCreateTaskModal(false)}
        documentData={documentData}
        onTaskSave={onSaveSuccess}
      />
      <PreviewModal
        show={showPreviewModal}
        onHide={handleClosePreviewModal}
        documentData={previewData}
        designUrl={previewData?.design_url ? `${previewData.design_url}` : null}
        placedFields={previewFields}
        modalSettingsObj={modalSettingsObj}
        scale={canvasSettings?.scale || 1}
      />
    </>
  );
};

export default Header;
